import React, { useEffect, useRef, useState } from 'react'
import { TabsList, TabsTrigger } from '@/Components/ui/tabs'
import type { BundleTab } from '@/types'
import { cn } from '@/lib/utils'
import { LazySvg } from '@/Components/custom/lazy-svg'
import useLocalizedValue from '@/hooks/use-localized-value'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { ScrollArea, ScrollBar } from '@/Components/ui/scroll-area'

interface BundleTabListProps {
  tabs: BundleTab[]
}

const BundleTabList: React.FC<BundleTabListProps> = React.memo(({ tabs }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Card width (158px) + gap (24px = 1.5rem)
  const CARD_WIDTH = 158
  const GAP = 24
  const SCROLL_AMOUNT = CARD_WIDTH + GAP

  const updateScrollIndicators = () => {
    const el = scrollRef.current
    if (!el) return

    // Get the actual scrollable viewport from ScrollArea
    const viewport = el.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement
    if (!viewport) return

    const { scrollLeft, scrollWidth, clientWidth } = viewport
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    // Get the actual scrollable viewport from ScrollArea
    const viewport = el.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement
    if (!viewport) return

    updateScrollIndicators()
    viewport.addEventListener('scroll', updateScrollIndicators, { passive: true })
    window.addEventListener('resize', updateScrollIndicators)
    return () => {
      viewport.removeEventListener('scroll', updateScrollIndicators)
      window.removeEventListener('resize', updateScrollIndicators)
    }
  }, [])

  return (
    <div className="relative hidden md:block">
      {/* Left chevron */}
      <AnimatePresence>
        {canScrollLeft && (
          <motion.div
            key="left-indicator"
            className="absolute inset-y-0 -left-3 top-1/2 z-10 -translate-y-1/2"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
          >
            <motion.div
              animate={{ x: [0, -3, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ChevronLeft
                className="size-5 cursor-pointer text-foreground/60"
                onClick={() => {
                  const viewport = scrollRef.current?.querySelector(
                    '[data-radix-scroll-area-viewport]'
                  ) as HTMLDivElement
                  viewport?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' })
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right chevron */}
      <AnimatePresence>
        {canScrollRight && (
          <motion.div
            key="right-indicator"
            className="absolute inset-y-0 -right-3 top-1/2 z-10 -translate-y-1/2"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
          >
            <motion.div
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ChevronRight
                className="size-5 cursor-pointer text-foreground/60"
                onClick={() => {
                  const viewport = scrollRef.current?.querySelector(
                    '[data-radix-scroll-area-viewport]'
                  ) as HTMLDivElement
                  viewport?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' })
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs List */}
      <ScrollArea ref={scrollRef} className="whitespace-nowrap">
        <TabsList
          className={cn(
            'flex h-[160px] gap-6',
            'scroll-snap-x scroll-snap-mandatory snap-mandatory',
            'overscroll-x-contain scroll-smooth'
          )}
        >
          {tabs.map((tab, idx) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={cn(
                'group inline-flex h-[140px] w-[158px] shrink-0 cursor-pointer items-center justify-center whitespace-pre-wrap rounded-3xl border border-border p-6 text-center text-foreground transition-colors duration-500 hover:border-primary hover:bg-primary hover:text-white',
                'scroll-snap-start',
                'data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow'
              )}
            >
              <div className="flex flex-col items-center gap-4">
                {tab.settings?.icon && (
                  <LazySvg
                    containsPath
                    name={tab.settings.icon}
                    className={cn(
                      'size-10 transition-colors',
                      'text-primary group-hover:text-primary-foreground',
                      'dark:text-primary',
                      'group-data-[state=active]:text-primary-foreground'
                    )}
                  />
                )}
                {tab.settings?.name && (
                  <div className="text-sm font-semibold capitalize">
                    {useLocalizedValue(tab.settings.name)}
                  </div>
                )}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  )
})

BundleTabList.displayName = 'BundleTabList'

export default BundleTabList
